import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import styles from './AdminShared.module.scss';
import { fetchSiteConfiguration, updatePlaybackConfiguration } from '../../../services/admin.service';
import { AdminPlaybackConfigurationUpdateDto, AdminSiteConfigurationDto } from '../../../types/admin.types';
import { Routes } from '../../../types/constants';
import { useUserState } from '../../../providers/user-state.provider.ts';
import { isSuperAdmin } from '../../../utils/roles';

const buildForm = (configuration: AdminSiteConfigurationDto): AdminPlaybackConfigurationUpdateDto => ({
    Id: configuration.Id,
    EnableMobileVideoPlayback: configuration.EnableMobileVideoPlayback ?? false,
    EnablePresignedVideoDelivery: configuration.EnablePresignedVideoDelivery ?? false,
    PresignedVideoUrlExpiryMinutes: configuration.PresignedVideoUrlExpiryMinutes ?? 360,
    EnableApiStreamFallback: configuration.EnableApiStreamFallback ?? true,
    ApiStreamTicketExpiryMinutes: configuration.ApiStreamTicketExpiryMinutes ?? 360,
    RequireApiStreamTicket: configuration.RequireApiStreamTicket ?? false
});

const hasPlayableDeliveryPath = (configuration: AdminPlaybackConfigurationUpdateDto) => (
    configuration.EnablePresignedVideoDelivery || configuration.EnableApiStreamFallback
);

/**
 * SuperAdministrator-only playback delivery controls kept separate from general site configuration.
 */
const AdminVideoPlayback = () => {
    const { getUserInfo } = useUserState();
    const userInfo = getUserInfo();
    const token = userInfo?.token;
    const canManagePlayback = isSuperAdmin(userInfo?.roles);
    const [form, setForm] = useState<AdminPlaybackConfigurationUpdateDto | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const loadConfiguration = useCallback(async () => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const response = await fetchSiteConfiguration(token);
            const nextForm = buildForm(response);
            setForm(nextForm);
            setErrorMessage(
                hasPlayableDeliveryPath(nextForm)
                    ? null
                    : 'At least one video playback delivery path must be enabled.'
            );
        } catch {
            setErrorMessage('Unable to load video playback settings.');
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (canManagePlayback) {
            loadConfiguration();
        }
    }, [canManagePlayback, loadConfiguration]);

    if (!canManagePlayback) {
        return <Navigate to={Routes.ADMIN_ANALYTICS} replace />;
    }

    const saveConfiguration = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!form) {
            return;
        }
        if (!hasPlayableDeliveryPath(form)) {
            setErrorMessage('At least one video playback delivery path must be enabled.');
            return;
        }

        setIsSaving(true);
        setErrorMessage(null);
        setSuccessMessage(null);
        try {
            await updatePlaybackConfiguration(form, token);
            setSuccessMessage('Video playback settings saved.');
        } catch {
            setErrorMessage('Unable to save video playback settings.');
        } finally {
            setIsSaving(false);
        }
    };

    const updateDeliveryFlag = (
        key: 'EnablePresignedVideoDelivery' | 'EnableApiStreamFallback',
        checked: boolean
    ) => {
        if (!form) {
            return;
        }

        const nextForm = { ...form, [key]: checked };
        if (!hasPlayableDeliveryPath(nextForm)) {
            // Playback rollout safety: both delivery switches off means playback-source and /watch both fail.
            setErrorMessage('At least one video playback delivery path must be enabled.');
            return;
        }

        setErrorMessage(null);
        setForm(nextForm);
    };

    const canSave = form ? hasPlayableDeliveryPath(form) && !isSaving : !isSaving;

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <h1>Video Playback</h1>
                    <p>Manage mobile playback, pre-signed delivery, and protected API stream fallback rollout.</p>
                </div>
            </header>

            {errorMessage ? <div className={styles.muted}>{errorMessage}</div> : null}
            {successMessage ? <div className={styles.muted}>{successMessage}</div> : null}

            {isLoading || !form ? (
                <section className={styles.panel}>
                    <div className={styles.muted}>Loading video playback settings...</div>
                </section>
            ) : (
                <section className={styles.panel}>
                    <form onSubmit={saveConfiguration}>
                        <h2>Delivery Controls</h2>
                        <p className={styles.muted}>
                            Keep mobile and pre-signed delivery behind these global switches while testing per-video overrides.
                        </p>
                        {!form.RequireApiStreamTicket ? (
                            <p className={styles.muted}>
                                Ticket enforcement is currently disabled so stale browser bundles can keep playing during rollout.
                                Enable it after the browser refresh window has passed.
                            </p>
                        ) : null}
                        <div className={styles.field}>
                            <label htmlFor="playback-enable-mobile">
                                <input
                                    id="playback-enable-mobile"
                                    type="checkbox"
                                    checked={form.EnableMobileVideoPlayback}
                                    onChange={(event) => setForm({ ...form, EnableMobileVideoPlayback: event.target.checked })}
                                />
                                Enable mobile video playback variants
                            </label>
                            <div className={styles.muted}>
                                Leave disabled until mobile MP4 transcodes have been validated on iPhone and Safari.
                            </div>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="playback-enable-presigned">
                                <input
                                    id="playback-enable-presigned"
                                    type="checkbox"
                                    checked={form.EnablePresignedVideoDelivery}
                                    onChange={(event) => updateDeliveryFlag('EnablePresignedVideoDelivery', event.target.checked)}
                                />
                                Enable pre-signed video delivery
                            </label>
                            <div className={styles.muted}>
                                Direct S3/CDN playback moves video bytes off the API while keeping playback URLs short lived.
                            </div>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="playback-presigned-expiry">Pre-signed video URL expiry (minutes)</label>
                            <input
                                id="playback-presigned-expiry"
                                type="number"
                                min="5"
                                max="1440"
                                step="5"
                                value={form.PresignedVideoUrlExpiryMinutes}
                                onChange={(event) => setForm({
                                    ...form,
                                    PresignedVideoUrlExpiryMinutes: event.target.value === '' ? 360 : Number(event.target.value)
                                })}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="playback-enable-api-fallback">
                                <input
                                    id="playback-enable-api-fallback"
                                    type="checkbox"
                                    checked={form.EnableApiStreamFallback}
                                    onChange={(event) => updateDeliveryFlag('EnableApiStreamFallback', event.target.checked)}
                                />
                                Allow ticket-protected API stream fallback
                            </label>
                            <div className={styles.muted}>
                                Leave this on during rollout. Turn it off after pre-signed delivery is verified globally.
                            </div>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="playback-api-ticket-expiry">API stream fallback ticket expiry (minutes)</label>
                            <input
                                id="playback-api-ticket-expiry"
                                type="number"
                                min="5"
                                max="1440"
                                step="5"
                                value={form.ApiStreamTicketExpiryMinutes}
                                onChange={(event) => setForm({
                                    ...form,
                                    ApiStreamTicketExpiryMinutes: event.target.value === '' ? 360 : Number(event.target.value)
                                })}
                            />
                            <div className={styles.muted}>
                                When ticket enforcement is enabled, this controls how long issued fallback URLs keep working.
                            </div>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="playback-require-api-ticket">
                                <input
                                    id="playback-require-api-ticket"
                                    type="checkbox"
                                    checked={form.RequireApiStreamTicket}
                                    onChange={(event) => setForm({ ...form, RequireApiStreamTicket: event.target.checked })}
                                />
                                Require ticket for API video streaming
                            </label>
                            <div className={styles.muted}>
                                When enabled, /v1/video/watch only streams when the URL has a signed ticket issued by the authorized playback-source endpoint.
                            </div>
                        </div>
                        <div className={styles.actions}>
                            <button className={styles.primary} type="submit" disabled={!canSave}>Save</button>
                            <button className={styles.secondary} type="button" onClick={loadConfiguration} disabled={isSaving}>
                                Reload
                            </button>
                        </div>
                    </form>
                </section>
            )}
        </div>
    );
};

export default AdminVideoPlayback;
